import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { PARTNER_FINANCE_CURRENCIES } from '../../utils/partnerFinance';
import { isHttpsUrl } from '../../utils/groupBilling';
import { CollectionMethodTiles, eyebrowSx, fieldSx } from './BillingParts';

const blank = (v) => (v === null || v === undefined ? '' : v);

function toForm(settings, schedule, currency) {
  return {
    currency: settings?.currency || currency || 'USD',
    defaultCollectionMethod: settings?.defaultCollectionMethod || 'in_app',
    defaultPaymentLinkUrl: blank(settings?.defaultPaymentLinkUrl),
    defaultPaymentLinkLabel: blank(settings?.defaultPaymentLinkLabel),
    offlineInstructions: blank(settings?.offlineInstructions),
    autoReminders: settings?.autoReminders !== false,
    depositType: schedule?.depositType || 'none',
    depositValue: blank(schedule?.depositValue),
    depositDueDate: blank(schedule?.depositDueDate),
    finalMode: schedule?.finalDueDate ? 'date' : 'days',
    finalDueDate: blank(schedule?.finalDueDate),
    finalDueDaysBefore: schedule ? blank(schedule.finalDueDaysBefore) : 60,
    installments: (schedule?.installments || []).map((i) => ({
      label: blank(i.label), dueDate: blank(i.dueDate), amountType: i.amountType || 'percent', value: blank(i.value),
    })),
  };
}

const GroupBillingSettingsDialog = ({ open, onClose, settings, schedule, currency, onSave, saving = false, error = '' }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => toForm(settings, schedule, currency));

  useEffect(() => {
    if (open) setForm(toForm(settings, schedule, currency));
  }, [open, settings, schedule, currency]);

  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const patchInstallment = (index, next) => setForm((prev) => ({
    ...prev,
    installments: prev.installments.map((inst, i) => (i === index ? { ...inst, ...next } : inst)),
  }));
  const linkInvalid = form.defaultCollectionMethod === 'external_link' && !isHttpsUrl(form.defaultPaymentLinkUrl);

  const submit = () => onSave({
    settings: {
      currency: form.currency,
      defaultCollectionMethod: form.defaultCollectionMethod,
      defaultPaymentLinkUrl: form.defaultCollectionMethod === 'external_link' ? form.defaultPaymentLinkUrl.trim() : null,
      defaultPaymentLinkLabel: form.defaultCollectionMethod === 'external_link' ? (form.defaultPaymentLinkLabel.trim() || null) : null,
      offlineInstructions: form.offlineInstructions || null,
      autoReminders: form.autoReminders,
    },
    schedule: {
      depositType: form.depositType,
      depositValue: form.depositType === 'none' ? 0 : form.depositValue,
      depositDueDate: form.depositType === 'none' ? null : (form.depositDueDate || null),
      finalDueDate: form.finalMode === 'date' ? (form.finalDueDate || null) : null,
      finalDueDaysBefore: form.finalMode === 'days' ? form.finalDueDaysBefore : null,
      installments: form.installments.filter((i) => i.dueDate),
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="group-settings-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        {t('groupBilling.settings.title')}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={2.5}>
          <FormControl size="small" sx={{ ...fieldSx, maxWidth: 200 }}>
            <InputLabel>{t('groupBilling.settings.currency')}</InputLabel>
            <Select label={t('groupBilling.settings.currency')} value={form.currency} onChange={(e) => patch({ currency: e.target.value })}>
              {PARTNER_FINANCE_CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.settings.defaultCollection')}</Typography>
            <CollectionMethodTiles
              value={form.defaultCollectionMethod}
              onChange={(v) => patch({ defaultCollectionMethod: v })}
              testIdPrefix="group-settings-method"
            />
          </Box>
          {form.defaultCollectionMethod === 'external_link' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('groupBilling.invoice.linkUrl')}
                value={form.defaultPaymentLinkUrl}
                onChange={(e) => patch({ defaultPaymentLinkUrl: e.target.value })}
                placeholder="https://"
                size="small"
                sx={fieldSx}
                error={Boolean(form.defaultPaymentLinkUrl) && linkInvalid}
                helperText={t('groupBilling.invoice.linkHint')}
                inputProps={{ 'data-testid': 'group-settings-link' }}
              />
              <TextField
                label={t('groupBilling.invoice.linkLabel')}
                value={form.defaultPaymentLinkLabel}
                onChange={(e) => patch({ defaultPaymentLinkLabel: e.target.value })}
                size="small"
                sx={fieldSx}
                inputProps={{ maxLength: 80 }}
              />
            </Box>
          )}
          <TextField
            label={t('groupBilling.invoice.offlineInstructions')}
            value={form.offlineInstructions}
            onChange={(e) => patch({ offlineInstructions: e.target.value })}
            multiline
            minRows={2}
            size="small"
            sx={fieldSx}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={form.autoReminders}
                onChange={(e) => patch({ autoReminders: e.target.checked })}
                inputProps={{ 'data-testid': 'group-settings-reminders' }}
              />
            )}
            label={(
              <Box>
                <Typography sx={{ fontSize: 14 }}>{t('groupBilling.settings.autoReminders')}</Typography>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{t('groupBilling.settings.autoRemindersHint')}</Typography>
              </Box>
            )}
          />

          <Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, pt: 2 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 19, mb: 0.5 }}>{t('groupBilling.schedule.title')}</Typography>
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>{t('groupBilling.schedule.hint')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl size="small" sx={fieldSx}>
                <InputLabel>{t('groupBilling.schedule.deposit')}</InputLabel>
                <Select
                  label={t('groupBilling.schedule.deposit')}
                  value={form.depositType}
                  onChange={(e) => patch({ depositType: e.target.value })}
                  inputProps={{ 'data-testid': 'group-schedule-deposit-type' }}
                >
                  <MenuItem value="none">{t('groupBilling.schedule.depositNone')}</MenuItem>
                  <MenuItem value="fixed_per_person">{t('groupBilling.schedule.depositFixed')}</MenuItem>
                  <MenuItem value="percent">{t('groupBilling.schedule.depositPercent')}</MenuItem>
                </Select>
              </FormControl>
              {form.depositType !== 'none' && (
                <>
                  <TextField
                    label={form.depositType === 'percent' ? t('groupBilling.schedule.percent') : t('groupBilling.schedule.perPerson', { currency: form.currency })}
                    type="number"
                    size="small"
                    sx={fieldSx}
                    value={form.depositValue}
                    onChange={(e) => patch({ depositValue: e.target.value })}
                    inputProps={{ min: 0, 'data-testid': 'group-schedule-deposit-value' }}
                  />
                  <TextField
                    label={t('groupBilling.schedule.depositDue')}
                    type="date"
                    size="small"
                    sx={fieldSx}
                    InputLabelProps={{ shrink: true }}
                    value={form.depositDueDate}
                    onChange={(e) => patch({ depositDueDate: e.target.value })}
                    helperText={t('groupBilling.schedule.depositDueHint')}
                  />
                </>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.schedule.installments')}</Typography>
              {form.installments.map((inst, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 1.2fr 1.2fr 1fr auto' }, gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                  <TextField size="small" sx={fieldSx} label={t('groupBilling.schedule.label')}
                    value={inst.label} onChange={(e) => patchInstallment(index, { label: e.target.value })} />
                  <TextField size="small" sx={fieldSx} type="date" label={t('groupBilling.schedule.dueDate')} InputLabelProps={{ shrink: true }}
                    value={inst.dueDate} onChange={(e) => patchInstallment(index, { dueDate: e.target.value })} />
                  <Select size="small" sx={{ borderRadius: 0 }} value={inst.amountType}
                    onChange={(e) => patchInstallment(index, { amountType: e.target.value })}>
                    <MenuItem value="percent">{t('groupBilling.schedule.percent')}</MenuItem>
                    <MenuItem value="fixed_per_person">{t('groupBilling.schedule.perPerson', { currency: form.currency })}</MenuItem>
                  </Select>
                  <TextField size="small" sx={fieldSx} type="number" label={t('groupBilling.schedule.value')}
                    value={inst.value} onChange={(e) => patchInstallment(index, { value: e.target.value })} />
                  <IconButton
                    aria-label={t('common.remove')}
                    onClick={() => patch({ installments: form.installments.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                sx={partnerLinkSx}
                onClick={() => patch({ installments: [...form.installments, { label: '', dueDate: '', amountType: 'percent', value: '' }] })}
                data-testid="group-schedule-add-installment"
              >
                {t('groupBilling.schedule.addInstallment')}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <FormControl size="small" sx={fieldSx}>
                <InputLabel>{t('groupBilling.schedule.final')}</InputLabel>
                <Select label={t('groupBilling.schedule.final')} value={form.finalMode} onChange={(e) => patch({ finalMode: e.target.value })}>
                  <MenuItem value="days">{t('groupBilling.schedule.finalDays')}</MenuItem>
                  <MenuItem value="date">{t('groupBilling.schedule.finalDate')}</MenuItem>
                </Select>
              </FormControl>
              {form.finalMode === 'days' ? (
                <TextField size="small" sx={fieldSx} type="number" label={t('groupBilling.schedule.daysBefore')}
                  value={form.finalDueDaysBefore} onChange={(e) => patch({ finalDueDaysBefore: e.target.value })}
                  inputProps={{ min: 0, 'data-testid': 'group-schedule-days-before' }} />
              ) : (
                <TextField size="small" sx={fieldSx} type="date" label={t('groupBilling.schedule.dueDate')} InputLabelProps={{ shrink: true }}
                  value={form.finalDueDate} onChange={(e) => patch({ finalDueDate: e.target.value })} />
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button onClick={submit} disabled={saving || linkInvalid} sx={partnerOxbloodBtnSx} data-testid="group-settings-save">
          {saving ? <CircularProgress size={20} sx={{ color: DOCUMENT.ivory }} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupBillingSettingsDialog;
